import { UnitType } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import * as React from "react";

export enum GuideLineType {
  HORIZONTAL = "horizontal",
  VERTICAL = "vertical"
}

export interface IGuideLineData {
  id: string;
  type: GuideLineType;
  position: number;
}

export interface IGuideLineProps {
  guideLine: IGuideLineData;
  zoomScale: number;
  unit: UnitType;
  onDrag: (id: string, position: number) => void;
  onRemove: (id: string) => void;
  containerWidth?: number;
  containerHeight?: number;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  scrollOffsetX?: number;
  scrollOffsetY?: number;
}

const convertPxToUnit = (px: number, unit: UnitType): number => {
  switch (unit) {
    case UnitType.MM:
      return px * 0.264583;
    case UnitType.INCH:
      return px / 96;
    case UnitType.PT:
      return px * 0.75;
    default:
      return px;
  }
};

const formatNumber = (value: number, unit: UnitType): string => {
  if (unit === UnitType.INCH) {
    return value.toFixed(1);
  }
  return Math.round(value).toString();
};

const getIsometricMode = (): boolean => {
  const storedValue = localStorage.getItem("isFreePositionAreaEnabled");
  if (storedValue !== null) {
    return storedValue !== "true";
  }
  return true;
};

const snapToIsometricGrid = (value: number): number => {
  if (!getIsometricMode()) {
    return value;
  }
  return Math.round(value / 10) * 10;
};

export const GuideLine = React.memo<IGuideLineProps>(
  ({
    guideLine,
    zoomScale,
    unit,
    onDrag,
    onRemove,
    containerWidth,
    containerHeight,
    isSelected,
    onSelect,
    scrollOffsetX = 0,
    scrollOffsetY = 0
  }) => {
    const lineRef = React.useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const dragOffsetRef = React.useRef(0);

    const handleMouseDown = React.useCallback(
      (e: React.MouseEvent) => {
        if (e.button === 2) {
          e.preventDefault();
          onRemove(guideLine.id);
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        onSelect?.(guideLine.id);

        const lineRect = lineRef.current?.getBoundingClientRect();
        if (!lineRect) return;

        const clickPos = guideLine.type === GuideLineType.HORIZONTAL ? e.clientY : e.clientX;
        const linePos = guideLine.type === GuideLineType.HORIZONTAL ? lineRect.top : lineRect.left;

        dragOffsetRef.current = clickPos - linePos;
        setIsDragging(true);
      },
      [guideLine.id, guideLine.type, onRemove, onSelect]
    );

    const handleMouseMove = React.useCallback(
      (e: MouseEvent) => {
        e.preventDefault();

        const canvasWrapper = lineRef.current?.parentElement;
        if (!canvasWrapper) return;

        const canvasRect = canvasWrapper.getBoundingClientRect();
        const currentPos = guideLine.type === GuideLineType.HORIZONTAL ? e.clientY : e.clientX;
        const canvasStart = guideLine.type === GuideLineType.HORIZONTAL ? canvasRect.top : canvasRect.left;
        const canvasSize =
          guideLine.type === GuideLineType.HORIZONTAL
            ? containerHeight || canvasRect.height
            : containerWidth || canvasRect.width;

        const relativePos = currentPos - canvasStart - dragOffsetRef.current;
        const clampedPos = Math.max(0, Math.min(relativePos, canvasSize));
        let newPosition = clampedPos / zoomScale;
        newPosition = snapToIsometricGrid(newPosition);

        onDrag(guideLine.id, newPosition);
      },
      [guideLine.id, guideLine.type, zoomScale, containerWidth, containerHeight, onDrag]
    );

    const handleIframeMouseMove = React.useCallback(
      (e: MouseEvent) => {
        const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
        if (!iframe) return;

        const iframeRect = iframe.getBoundingClientRect();
        const canvasWrapper = lineRef.current?.parentElement;
        if (!canvasWrapper) return;

        const canvasRect = canvasWrapper.getBoundingClientRect();

        const currentPos =
          guideLine.type === GuideLineType.HORIZONTAL ? e.clientY + iframeRect.top : e.clientX + iframeRect.left;
        const canvasStart = guideLine.type === GuideLineType.HORIZONTAL ? canvasRect.top : canvasRect.left;
        const canvasSize =
          guideLine.type === GuideLineType.HORIZONTAL
            ? containerHeight || canvasRect.height
            : containerWidth || canvasRect.width;

        const relativePos = currentPos - canvasStart - dragOffsetRef.current;
        const clampedPos = Math.max(0, Math.min(relativePos, canvasSize));
        let newPosition = clampedPos / zoomScale;
        newPosition = snapToIsometricGrid(newPosition);

        onDrag(guideLine.id, newPosition);
      },
      [guideLine.id, guideLine.type, zoomScale, containerWidth, containerHeight, onDrag]
    );

    const handleMouseUp = React.useCallback(() => {
      setIsDragging(false);
    }, []);

    React.useEffect(() => {
      if (isDragging) {
        const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
        const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        if (iframeDoc) {
          iframeDoc.addEventListener("mousemove", handleIframeMouseMove as any);
          iframeDoc.addEventListener("mouseup", handleMouseUp as any);
        }

        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);

          if (iframeDoc) {
            iframeDoc.removeEventListener("mousemove", handleIframeMouseMove as any);
            iframeDoc.removeEventListener("mouseup", handleMouseUp as any);
          }
        };
      }
    }, [isDragging, handleMouseMove, handleIframeMouseMove, handleMouseUp]);

    const position = guideLine.position * zoomScale;
    const isHorizontal = guideLine.type === GuideLineType.HORIZONTAL;

    const adjustedPosition = isHorizontal ? position - scrollOffsetY * zoomScale : position - scrollOffsetX * zoomScale;

    const unitValue = convertPxToUnit(guideLine.position, unit);
    const displayValue = formatNumber(unitValue, unit);

    const lineStyle: React.CSSProperties = isHorizontal
      ? {
          position: "absolute",
          top: `${adjustedPosition}px`,
          left: 0,
          width: containerWidth ? `${containerWidth}px` : "100%",
          height: "1px",
          backgroundColor: isSelected ? "#ff6b00" : "var(--guide-line-color, #00a8ff)",
          cursor: "ns-resize",
          zIndex: isDragging ? 1001 : isSelected ? 1000 : 999,
          pointerEvents: "auto",
          boxShadow: isDragging
            ? "0 0 6px rgba(0, 168, 255, 0.8)"
            : isSelected
              ? "0 0 4px rgba(255, 107, 0, 0.6)"
              : undefined,
          willChange: isDragging ? "top" : "auto"
        }
      : {
          position: "absolute",
          left: `${adjustedPosition}px`,
          top: 0,
          height: containerHeight ? `${containerHeight}px` : "100%",
          width: "1px",
          backgroundColor: isSelected ? "#ff6b00" : "var(--guide-line-color, #00a8ff)",
          cursor: "ew-resize",
          zIndex: isDragging ? 1001 : isSelected ? 1000 : 999,
          pointerEvents: "auto",
          boxShadow: isDragging
            ? "0 0 6px rgba(0, 168, 255, 0.8)"
            : isSelected
              ? "0 0 4px rgba(255, 107, 0, 0.6)"
              : undefined,
          willChange: isDragging ? "left" : "auto"
        };

    const labelStyle: React.CSSProperties = isHorizontal
      ? {
          position: "absolute",
          left: "4px",
          top: "-10px",
          fontSize: "10px",
          color: isSelected ? "#ff6b00" : "var(--guide-line-color, #00a8ff)",
          backgroundColor: "var(--dark-0)",
          padding: "1px 4px",
          borderRadius: "var(--radius-xs)",
          pointerEvents: "auto",
          whiteSpace: "nowrap",
          fontWeight: 500
        }
      : {
          position: "absolute",
          top: "4px",
          left: "2px",
          fontSize: "10px",
          color: isSelected ? "#ff6b00" : "var(--guide-line-color, #00a8ff)",
          backgroundColor: "var(--dark-0)",
          padding: "1px 4px",
          borderRadius: "var(--radius-xs)",
          pointerEvents: "auto",
          whiteSpace: "nowrap",
          writingMode: "vertical-lr",
          transform: "rotate(180deg)",
          fontWeight: 500
        };

    return (
      <div
        ref={lineRef}
        className={`guide-line guide-line-${guideLine.type} ${isDragging ? "dragging" : ""} ${
          isSelected ? "selected" : ""
        }`}
        id={`guide-line`}
        style={lineStyle}
        data-id={guideLine.id}
        onMouseDown={handleMouseDown}
        onContextMenu={(e) => e.preventDefault()}
        title="Sağ tık veya Delete tuşu ile kaldır"
      >
        {(isDragging || isSelected) && <span style={labelStyle}>{displayValue}</span>}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.guideLine.position === nextProps.guideLine.position &&
      prevProps.zoomScale === nextProps.zoomScale &&
      prevProps.unit === nextProps.unit &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.containerWidth === nextProps.containerWidth &&
      prevProps.containerHeight === nextProps.containerHeight &&
      prevProps.scrollOffsetX === nextProps.scrollOffsetX &&
      prevProps.scrollOffsetY === nextProps.scrollOffsetY
    );
  }
);

GuideLine.displayName = "GuideLine";
